/**
 * Stage 23 · Global Partner Network. Suntronix-internal only.
 * Tabs: Directory · Work Orders · Selection · Scorecard · Quality · Documents.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Truck, Plus, Search, Filter, Send, ShieldAlert,
  BadgeCheck, FileText, Radar, ChevronRight, RefreshCw, Star,
} from "lucide-react";

const TABS = [
  { key: "directory",  label: "Directory",   icon: Building2 },
  { key: "orders",     label: "Work orders", icon: Send },
  { key: "select",     label: "Selection engine", icon: Radar },
  { key: "scorecard",  label: "Scorecard",   icon: BadgeCheck },
  { key: "ratings",    label: "Ratings",     icon: Star },
  { key: "quality",    label: "Quality",     icon: ShieldAlert },
  { key: "documents",  label: "Documents",   icon: FileText },
];
const KINDS = ["courier","warehouse","procurement_vendor","device_vendor","authorised_service_centre",
  "repair_partner","field_engineer","installation_partner","itad_partner","recycler",
  "data_erasure_partner","buyback_partner","packaging_partner","other"];

export default function PartnerNetwork() {
  const [tab, setTab] = useState("directory");
  return (
    <div className="max-w-6xl space-y-5" data-testid="partner-network">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Partners · Stage 23</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Global partner network</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Every courier, warehouse, service centre, ITAD and installation partner — governed centrally with chain-of-custody visibility, partner-specific work orders and scorecards.
        </p>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`pn-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {tab === "directory"  && <Directory />}
      {tab === "orders"     && <WorkOrders />}
      {tab === "select"     && <SelectionTab />}
      {tab === "scorecard"  && <ScorecardTab />}
      {tab === "ratings"    && <RatingsTab />}
      {tab === "quality"    && <QualityTab />}
      {tab === "documents"  && <DocumentsTab />}
    </div>
  );
}

/* -------- Ratings -------- */
function RatingsTab() {
  const [ranking, setRanking] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reviews, setReviews] = useState({ items: [], aggregate: { count: 0, average: null } });
  useEffect(() => {
    api.get("/partners/ranking", { params: { limit: 25 } }).then(({ data }) => {
      setRanking(data.items || []);
      if ((data.items || []).length) setSelected(data.items[0].partner_id);
    });
  }, []);
  useEffect(() => {
    if (!selected) return;
    api.get(`/partners/${selected}/ratings`).then(({ data }) => setReviews(data)).catch(() => setReviews({ items: [], aggregate: { count: 0, average: null } }));
  }, [selected]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="pn-ratings">
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">Top-rated partners</div>
        {ranking.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No ratings yet. Customers can rate any completed PWO.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr><th className="text-left py-2 px-3">Partner</th><th className="text-right py-2 px-3">Rating</th><th className="text-right py-2 px-3">Reviews</th></tr>
            </thead>
            <tbody>
              {ranking.map((r) => (
                <tr key={r.partner_id} className={"border-t border-slate-100 cursor-pointer " + (selected === r.partner_id ? "bg-slate-50" : "")}
                    onClick={() => setSelected(r.partner_id)} data-testid={`pn-rank-${r.partner_id}`}>
                  <td className="py-2 px-3">
                    <div className="text-slate-900">{r.trading_name || r.legal_name || "—"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.kind}</div>
                  </td>
                  <td className="py-2 px-3 text-right"><Stars value={r.avg_rating} /></td>
                  <td className="py-2 px-3 text-right text-slate-700 tabular-nums">{r.rating_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900 flex items-center justify-between">
          <span>Customer reviews {selected ? `· ${selected.slice(0, 8)}` : ""}</span>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            {reviews.aggregate?.average != null ? `${reviews.aggregate.average} ★ · ${reviews.aggregate.count} reviews` : "no data"}
          </span>
        </div>
        {reviews.items?.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Select a partner to see their reviews.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(reviews.items || []).map((r) => (
              <li key={r.id || r.pwo_id + r.rater_email} className="p-3">
                <div className="flex items-center gap-2 text-sm">
                  <Stars value={r.stars} />
                  <span className="text-slate-500 text-xs">by {r.rater_email}</span>
                  <span className="text-slate-400 text-xs">· PWO {r.pwo_id?.slice(0, 8)}</span>
                  <span className="ml-auto text-[10px] text-slate-400">{r.created_at?.slice(0, 10)}</span>
                </div>
                {r.comment && <div className="text-slate-800 text-sm mt-1">{r.comment}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stars({ value }) {
  const v = Math.round(Number(value) || 0);
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="pn-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={"h-3.5 w-3.5 " + (i <= v ? "fill-amber-400 text-amber-400" : "text-slate-300")} />
      ))}
      <span className="ml-1 text-xs text-slate-700 tabular-nums">{value != null ? Number(value).toFixed(2) : "—"}</span>
    </span>
  );
}
{/* END RATINGS */}

/* -------- Directory -------- */
function Directory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(""); const [kind, setKind] = useState("");
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/partners/directory", { params: { q: q || undefined, kind: kind || undefined } })
    .then(({ data }) => setItems(data.items || [])), [kind, q]);
  useEffect(() => { load().catch(() => {}); }, [q, kind, load]);
  return (
    <div className="space-y-3" data-testid="pn-directory">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input className="input pl-9 w-full" placeholder="Search partners" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pn-search" />
        </div>
        <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="pn-kind">
          <option value="">All kinds</option>
          {KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
        </select>
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="pn-new">
          <Plus className="h-4 w-4" /> New partner
        </button>
      </div>
      {showForm && <PartnerForm onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left py-2 px-3">Partner</th>
              <th className="text-left py-2 px-3">Kind</th>
              <th className="text-left py-2 px-3">Countries</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Risk</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No partners yet.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`pn-row-${p.partner_number}`}>
                <td className="py-2 px-3">
                  <div className="text-slate-900 font-medium">{p.trading_name || p.legal_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{p.partner_number}</div>
                </td>
                <td className="py-2 px-3 text-slate-600">{p.kind?.replace(/_/g, " ")}</td>
                <td className="py-2 px-3 text-slate-600">{(p.countries || []).join(", ") || "—"}</td>
                <td className="py-2 px-3"><span className={statusChip(p.status)}>{p.status}</span></td>
                <td className="py-2 px-3"><span className={riskChip(p.risk_status)}>{p.risk_status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PartnerForm({ onCancel, onCreated }) {
  const [f, setF] = useState({ kind: "courier", legal_name: "", trading_name: "", countries: "", services: "", capabilities: "", status: "prospect", risk_status: "low", internal_sla_days: 3, sla_days: 5 });
  const save = async () => {
    try {
      await api.post("/partners/directory", {
        ...f,
        countries: f.countries.split(",").map((x) => x.trim()).filter(Boolean),
        services: f.services.split(",").map((x) => x.trim()).filter(Boolean),
        capabilities: f.capabilities.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Partner created"); onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="pn-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Legal name</div>
          <input className="input" value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} data-testid="pn-legal-name" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Trading name</div>
          <input className="input" value={f.trading_name} onChange={(e) => setF({ ...f, trading_name: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="pn-form-kind">
            {KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
          </select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Status</div>
          <select className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            {["prospect","review","approved","active","suspended","expired","terminated"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Countries (comma sep)</div>
          <input className="input" value={f.countries} onChange={(e) => setF({ ...f, countries: e.target.value })} placeholder="IN, UK, SG" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Services (comma sep)</div>
          <input className="input" value={f.services} onChange={(e) => setF({ ...f, services: e.target.value })} placeholder="deliver, pickup" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Capabilities</div>
          <input className="input" value={f.capabilities} onChange={(e) => setF({ ...f, capabilities: e.target.value })} placeholder="same_day, high_value" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Risk</div>
          <select className="input" value={f.risk_status} onChange={(e) => setF({ ...f, risk_status: e.target.value })}>
            {["low","medium","high","critical"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="pn-save">Create partner</button>
      </div>
    </div>
  );
}

/* -------- Work Orders -------- */
function WorkOrders() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(() => api.get("/partners/work-orders", { params: { status: status || undefined } }).then(({ data }) => setItems(data.items || [])), [status]);
  useEffect(() => { load().catch(() => {}); }, [load, status]);
  const transition = async (id, s) => {
    try { await api.patch(`/partners/work-orders/${id}`, { status: s, reject_reason: s === "rejected" ? "manual override" : undefined }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="pn-work-orders">
      <div className="flex items-center gap-2">
        <select className="input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pwo-status">
          <option value="">All statuses</option>
          {["assigned","accepted","scheduled","in_progress","completed","rejected","exception"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white inline-flex items-center gap-1"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">PWO</th><th className="text-left py-2 px-3">Service</th><th className="text-left py-2 px-3">Country</th><th className="text-left py-2 px-3">Status</th><th className="text-right py-2 px-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No work orders.</td></tr>
            ) : items.map((w) => (
              <tr key={w.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900">{w.pwo_number}</td>
                <td className="py-2 px-3 text-slate-700">{w.service}</td>
                <td className="py-2 px-3 text-slate-600">{w.country || "—"}</td>
                <td className="py-2 px-3"><span className={pwoChip(w.status)}>{w.status}</span></td>
                <td className="py-2 px-3 text-right text-xs space-x-1">
                  {["accepted","scheduled","in_progress","completed","rejected"].map((s) => (
                    <button key={s} onClick={() => transition(w.id, s)} disabled={w.status === s}
                      className="px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40" data-testid={`pwo-${w.id}-${s}`}>
                      {s}
                    </button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------- Selection Engine -------- */
function SelectionTab() {
  const [service, setService] = useState("deliver"); const [country, setCountry] = useState("IN");
  const [result, setResult] = useState(null);
  const run = async () => {
    try { const { data } = await api.post("/partners/select", { service, country }); setResult(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="pn-selection">
      <div className="bg-white border border-slate-200 rounded-md p-3 flex items-end gap-2 flex-wrap">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Service</div>
          <input className="input" value={service} onChange={(e) => setService(e.target.value)} data-testid="sel-service" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Country</div>
          <input className="input" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="sel-country" /></label>
        <button onClick={run} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="sel-run">Recommend</button>
      </div>
      {result && (
        <div className="bg-white border border-slate-200 rounded-md p-3 text-xs text-slate-600">
          <div className="italic mb-3">{result.reasoning}</div>
          {result.candidates.length === 0 ? <div className="text-slate-500">No matching active partners.</div> : (
            <ul className="space-y-2">
              {result.candidates.map((c, i) => (
                <li key={i} className="flex items-center justify-between border-b border-slate-100 pb-1">
                  <div>
                    <div className="text-slate-900 font-medium">{c.partner.trading_name || c.partner.legal_name}</div>
                    <div className="text-[10px] text-slate-500">{(c.partner.countries || []).join(", ")}</div>
                  </div>
                  <div className="text-lg font-heading font-semibold tabular-nums text-slate-900">{c.score}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* -------- Scorecard -------- */
function ScorecardTab() {
  const [items, setItems] = useState([]); const [pid, setPid] = useState(""); const [card, setCard] = useState(null);
  useEffect(() => { api.get("/partners/directory").then(({ data }) => { setItems(data.items || []); setPid(data.items?.[0]?.id || ""); }); }, []);
  useEffect(() => { if (!pid) return; api.get(`/partners/scorecard/${pid}`).then(({ data }) => setCard(data)); }, [pid]);
  return (
    <div className="space-y-3" data-testid="pn-scorecard">
      <select className="input min-w-[280px]" value={pid} onChange={(e) => setPid(e.target.value)} data-testid="pn-scorecard-partner">
        {items.map((p) => <option key={p.id} value={p.id}>{p.trading_name || p.legal_name}</option>)}
      </select>
      {card && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Assigned" value={card.jobs.assigned} />
          <Tile label="Accepted" value={card.jobs.accepted} />
          <Tile label="Completed" value={card.jobs.completed} />
          <Tile label="Exceptions" value={card.jobs.exceptions} />
          <Tile label="On-time %"   value={card.on_time_pct ?? "—"} />
          <Tile label="Acceptance %" value={card.acceptance_pct ?? "—"} />
          <Tile label="Completion %" value={card.completion_pct ?? "—"} />
          <Tile label="Open quality" value={card.quality_open} />
          <Tile label="Avg rating"   value={card.avg_rating ?? "—"} />
          <Tile label="Reviews"      value={card.rating_count ?? 0} />
        </div>
      )}
    </div>
  );
}
function Tile({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-heading font-semibold tabular-nums text-slate-900">{value ?? "—"}</div>
    </div>
  );
}

/* -------- Quality -------- */
function QualityTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/partners/quality").then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="pn-quality">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
          <tr><th className="text-left py-2 px-3">Partner</th><th className="text-left py-2 px-3">Kind</th><th className="text-left py-2 px-3">Severity</th><th className="text-left py-2 px-3">Status</th></tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={4} className="py-6 text-center text-slate-500">No quality issues.</td></tr>
          ) : items.map((q) => (
            <tr key={q.id} className="border-t border-slate-100">
              <td className="py-2 px-3 text-slate-700 font-mono">{q.partner_id?.slice(0, 8)}</td>
              <td className="py-2 px-3 text-slate-700">{q.kind}</td>
              <td className="py-2 px-3"><span className={riskChip(q.severity)}>{q.severity}</span></td>
              <td className="py-2 px-3 text-slate-600">{q.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------- Documents / Alerts -------- */
function DocumentsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/partners/alerts/expiring", { params: { days: 90 } }).then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="pn-docs">
      <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">Documents expiring in 90 days</div>
      {items.length === 0 ? <div className="p-6 text-sm text-slate-500">Nothing expiring.</div> : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Partner</th><th className="text-left py-2 px-3">Kind</th><th className="text-left py-2 px-3">Name</th><th className="text-right py-2 px-3">Expires</th></tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-700">{d.partner_id?.slice(0, 8)}</td>
                <td className="py-2 px-3 text-slate-700">{d.kind}</td>
                <td className="py-2 px-3 text-slate-900">{d.name}</td>
                <td className="py-2 px-3 text-right tabular-nums text-slate-600">{d.expires_at?.slice(0, 10) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function statusChip(s) {
  const m = { active: "bg-emerald-100 text-emerald-800 border-emerald-200", suspended: "bg-amber-100 text-amber-800 border-amber-200", terminated: "bg-rose-100 text-rose-800 border-rose-200", prospect: "bg-slate-100 text-slate-600 border-slate-200", review: "bg-sky-100 text-sky-800 border-sky-200", approved: "bg-emerald-50 text-emerald-800 border-emerald-200", expired: "bg-slate-100 text-slate-500 border-slate-200" };
  return "inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (m[s] || m.prospect);
}
function riskChip(s) {
  const m = { low: "bg-emerald-100 text-emerald-800 border-emerald-200", medium: "bg-amber-100 text-amber-800 border-amber-200", high: "bg-orange-100 text-orange-800 border-orange-200", critical: "bg-rose-100 text-rose-800 border-rose-200" };
  return "inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (m[s] || m.low);
}
function pwoChip(s) {
  const m = { assigned: "bg-sky-100 text-sky-800 border-sky-200", accepted: "bg-emerald-50 text-emerald-800 border-emerald-200", completed: "bg-emerald-100 text-emerald-800 border-emerald-200", rejected: "bg-rose-100 text-rose-800 border-rose-200", exception: "bg-orange-100 text-orange-800 border-orange-200", in_progress: "bg-amber-50 text-amber-800 border-amber-200", scheduled: "bg-slate-100 text-slate-700 border-slate-200" };
  return "inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (m[s] || m.assigned);
}
